<script setup>
import store from "@/store/store";
import axios from "axios";
import { ref, computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const router = useRouter();
const toast = useToast();
const { t } = useI18n();
const isSent = ref(false);
const isLoading = ref(false);
const isLoaded = ref(false);
const user = computed(() => store.state.auth.user);

onMounted(() => {
  if (user.value.hasEmailConfirmed || user.value.isTemporary) {
    router.push("/new-document");
  } else {
    isLoaded.value = true;
  }
});

const handleResendEmail = async () => {
  try {
    isLoading.value = true;
    await axios.post("/api/user/resend-verification-email");
    toast.success(t("notifications.verification_email_sent"));
    isSent.value = true;
  } catch (error) {
    toast.error(t("notifications.default_error"));
  } finally {
    isLoading.value = false;
  }
};
</script>
<template>
  <div v-if="isLoaded" class="auth-container">
    <!-- Logo Section -->
    <div class="logo-section">
      <router-link to="/login">
        <img src="/images/logos/legalTorchLogo.svg" alt="Legal Torch Logo" class="logo clickable" />
      </router-link>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <div class="main-content">
        <h2 class="form-title">{{ $t("auth.verification_required") }}</h2>
        <p class="form-subtitle">{{ $t("auth.verification_subtitle") }}</p>

        <!-- Email Info Card -->
        <div class="email-info-card">
          <div class="email-info-content">
            <p class="email-sent-text" v-html="$t('auth.verification_email_sent', { email: `<strong>${user.email}</strong>` })"></p>
          </div>
        </div>

        <!-- Resend Instructions -->
        <p class="resend-instructions">
          {{ $t("auth.verification_resend_instruction") }}
        </p>

        <!-- Resend Button -->
        <button
          :disabled="isSent || isLoading"
          class="submit-btn"
          :class="{ loading: isLoading, sent: isSent }"
          @click="handleResendEmail"
        >
          <div v-if="isLoading" class="loading-content">
            <div class="spinner"></div>
            <span>{{ $t("common.sending") || "Sending..." }}</span>
          </div>
          <span v-else-if="isSent" class="sent-content">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20 6L9 17L4 12"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ $t("common.sent") || "Sent!" }}
          </span>
          <span v-else>{{ $t("auth.resend_email") }}</span>
        </button>

        <!-- Bottom Link -->
        <div class="bottom-link">
          <router-link to="/login" class="link">
            {{ $t("auth.back_to_login") || "Back to login" }}
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.auth-container {
  min-height: 100vh;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.logo-section {
  margin-bottom: 40px;
}

.logo {
  width: 256px;
  height: 23px;

  &.clickable {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }
}

.content-wrapper {
  width: 100%;
  max-width: 500px;
  display: flex;
  justify-content: center;
}

.main-content {
  flex: 1;
  width: 100%;
  text-align: center;
}

.verification-icon {
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
}

.email-icon {
  width: 80px;
  height: 80px;
  background-color: #eff6ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dbeafe;
}

.form-title {
  text-align: center;
  font-size: 40px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  line-height: 1.2;
}

.form-subtitle {
  text-align: center;
  font-size: 18px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.5;
}

.email-info-card {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
}

.email-info-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.email-sent-text {
  font-size: 16px;
  color: #374151;
  margin: 0;
  line-height: 1.5;
}

.resend-instructions {
  text-align: center;
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.5;
}

.submit-btn {
  width: 100%;
  max-width: 320px;
  background-color: #d8ed84;
  color: #1f2937;
  border: none;
  border-radius: 16px;
  padding: 16px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 0 auto 24px auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: #c9e16f;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
  }

  &.loading {
    background-color: rgba(216, 237, 132, 0.7);
    cursor: wait;
  }

  &.sent {
    background-color: #10b981;
    color: white;

    &:hover {
      background-color: #059669;
    }
  }

  .loading-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .sent-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .spinner {
    width: 20px;
    height: 20px;
    border: 2px solid transparent;
    border-top: 2px solid currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    flex-shrink: 0;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.bottom-link {
  text-align: center;
  margin-top: 24px;
}

.link {
  color: #2563eb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #1d4ed8;
    text-decoration: underline;
  }
}

@media (max-width: 768px) {
  .auth-container {
    padding: 24px 16px;
  }

  .logo {
    width: 200px;
    height: 18px;
  }

  .content-wrapper {
    width: 100%;
    max-width: 100%;
  }

  .main-content {
    width: 100%;
    max-width: 100%;
  }

  .form-title {
    font-size: 28px;
  }

  .form-subtitle {
    font-size: 16px;
  }

  .email-info-card {
    padding: 16px;
  }

  .email-sent-text {
    font-size: 14px;
  }

  .resend-instructions {
    font-size: 14px;
  }
}
</style>
